import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import { usePrimaryWallet } from '@/hooks/useWalletQueries';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import {
  isMobileDevice,
  generatePhantomConnectDeeplink,
  generateBackpackInAppBrowserUrl,
  generateSolflareInAppBrowserUrl,
  generateMagicEdenInAppBrowserUrl,
  savePendingConnection,
  clearPendingConnection,
  redirectToMobileWallet,
  isInSolflareInAppBrowser,
  isInBackpackInAppBrowser,
  isInMagicEdenInAppBrowser,
} from '@/lib/mobileWalletUtils';
import {
  parsePhantomConnectResponse,
  processPhantomConnectResponse,
  clearPhantomSession,
} from '@/lib/phantomMobileEncryption';
import { mwaProvider } from '@/lib/mwaAdapter';
import useWalletMobilePhantom from '@/hooks/useWalletMobilePhantom';
import useWalletMobileMWA from '@/hooks/useWalletMobileMWA';
import InAppBrowserWalletMismatchModal from '@/components/InAppBrowserWalletMismatchModal';
import MobileWalletNavigationModal from '@/components/MobileWalletNavigationModal';
import useMobileWalletNavStore from '@/stores/useMobileWalletNavStore';
import { useAuthStore } from '@/hooks/useAuthStore';
import useModalStore from '@/stores/useModalStore';
import { captureSentryException } from '@/config/sentryConfig';

export type WalletAdapterName = 'Phantom' | 'Solflare' | 'Backpack' | 'MagicEden' | 'MWA';

interface PhantomProvider {
  isPhantom?: boolean;
  connect: () => Promise<{ publicKey: PublicKey }>;
  disconnect: () => Promise<void>;
  signTransaction: <T extends Transaction | VersionedTransaction>(transaction: T) => Promise<T>;
  signAllTransactions: <T extends Transaction | VersionedTransaction>(transactions: T[]) => Promise<T[]>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
  publicKey: PublicKey | null;
  isConnected: boolean;
  on: (event: string, callback: (args: any) => void) => void;
  off: (event: string, callback: (args: any) => void) => void;
}

interface SolflareProvider {
  isSolflare?: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  signTransaction: <T extends Transaction | VersionedTransaction>(transaction: T) => Promise<T>;
  signAllTransactions: <T extends Transaction | VersionedTransaction>(transactions: T[]) => Promise<T[]>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
  publicKey: PublicKey | null;
  isConnected: boolean;
  on: (event: string, callback: (args: any) => void) => void;
  off: (event: string, callback: (args: any) => void) => void;
}

interface BackpackProvider {
  isBackpack?: boolean;
  connect: () => Promise<{ publicKey: PublicKey }>;
  disconnect: () => Promise<void>;
  signTransaction: <T extends Transaction | VersionedTransaction>(transaction: T) => Promise<T>;
  signAllTransactions: <T extends Transaction | VersionedTransaction>(transactions: T[]) => Promise<T[]>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
  publicKey: PublicKey | null;
  isConnected: boolean;
  on: (event: string, callback: (args: any) => void) => void;
  off: (event: string, callback: (args: any) => void) => void;
}

interface MagicEdenProvider {
  isMagicEden?: boolean;
  connect: () => Promise<{ publicKey: PublicKey }>;
  disconnect: () => Promise<void>;
  signTransaction: <T extends Transaction | VersionedTransaction>(transaction: T) => Promise<T>;
  signAllTransactions: <T extends Transaction | VersionedTransaction>(transactions: T[]) => Promise<T[]>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
  publicKey: PublicKey | null;
  isConnected: boolean;
  on: (event: string, callback: (args: any) => void) => void;
  off: (event: string, callback: (args: any) => void) => void;
}

interface MWAProvider {
  connect: () => Promise<{ publicKey: PublicKey }>;
  disconnect: () => Promise<void>;
  signTransaction: <T extends Transaction | VersionedTransaction>(transaction: T) => Promise<T>;
  signAllTransactions: <T extends Transaction | VersionedTransaction>(transactions: T[]) => Promise<T[]>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
  publicKey: PublicKey | null;
  isConnected: boolean;
  on: (event: string, callback: (args: any) => void) => void;
  off: (event: string, callback: (args: any) => void) => void;
}

declare global {
  interface Window {
    phantom?: {
      solana?: PhantomProvider;
    };
    solflare?: SolflareProvider;
    backpack?: {
      solana?: BackpackProvider;
    };
    magicEden?: {
      solana?: MagicEdenProvider;
    };
    mwa?: MWAProvider;
  }
}

interface WalletContextState {
  publicKey: PublicKey | null;
  connected: boolean;
  connecting: boolean;
  disconnecting: boolean;
  wallet: WalletAdapterName;
  connect: (walletName: WalletAdapterName) => Promise<PublicKey>;
  disconnect: () => Promise<void>;
  select: (walletName: WalletAdapterName) => void;
  signTransaction: <T extends Transaction | VersionedTransaction>(transaction: T) => Promise<T>;
  signAllTransactions: <T extends Transaction | VersionedTransaction>(transactions: T[]) => Promise<T[]>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
  sendTransaction: (transaction: Transaction, connection: any, options?: any) => Promise<string>;
}

const WalletContext = createContext<WalletContextState | undefined>(undefined);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within WalletProvider');
  }
  return context;
};

interface WalletProviderProps {
  children: ReactNode;
}

export const WalletContextProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const [publicKey, setPublicKey] = useState<PublicKey | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(() => false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<WalletAdapterName>('Phantom');
  const [mismatchModalVisible, setMismatchModalVisible] = useState(false);
  const [requiredWalletType, setRequiredWalletType] = useState<WalletAdapterName | null>(null);
  const [shownMobileNavModals, setShownMobileNavModals] = useState<Set<string>>(new Set());
  const [isManualDisconnect, setIsManualDisconnect] = useState(false);
  const { data: primaryWalletData } = usePrimaryWallet();
  const primaryWallet = primaryWalletData?.wallet;
  const { toast } = useToast();
  const { isConnecting: isConnectingPhantom, setIsConnecting: setIsConnectingPhantom, hasValidPhantomSession } = useWalletMobilePhantom();
  const { isConnecting: isConnectingMWA, setIsConnecting: setIsConnectingMWA, hasValidMWASession } = useWalletMobileMWA();
  const { visible: mobileNavVisible, walletType: mobileNavWalletType, context: mobileNavContext, onChangePrimary: mobileNavOnChangePrimary, showModal: showMobileNavModal, hideModal: hideMobileNavModal } = useMobileWalletNavStore();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const sessionValidated = useAuthStore(state => state.sessionValidated);
  const setWalletMismatchModal = useModalStore((state) => state.setWalletMismatchModal);

  // Auto-reconnect effect with proper guards to prevent connection loop
  useEffect(function autoReconnectPrimaryWallet() {
    if (!primaryWallet || !primaryWallet.walletAdapter || primaryWallet?.walletType !== 'connected' || !isAuthenticated || !sessionValidated) {
      return;
    }

    // Guard: If mobile nav modal is visible, don't do anything (wait for user)
    if (mobileNavVisible) {
      return;
    }

    const isMobile = isMobileDevice();

    // MOBILE NAVIGATION: Show modal if on mobile and primary wallet is Solflare/Backpack/MagicEden
    if (isMobile) {
      const walletAdapter = primaryWallet.walletAdapter.toLowerCase();
      if (walletAdapter === 'solflare' || walletAdapter === 'backpack' || walletAdapter === 'magiceden') {
        // Check if user is in ANY wallet in-app browser
        const isInAnyWalletBrowser = isInSolflareInAppBrowser() || isInBackpackInAppBrowser() || isInMagicEdenInAppBrowser();

        // If in any wallet browser (even if wrong one), skip navigation modal
        // Let InAppBrowserWalletMismatchModal handle it instead
        if (isInAnyWalletBrowser) {
          // Don't show navigation modal, continue to mismatch check below
        } else {
          // Not in any wallet browser - show navigation modal (with state check)
          const walletType = walletAdapter === 'solflare' ? 'Solflare'
                           : walletAdapter === 'backpack' ? 'Backpack'
                           : 'MagicEden';

          if (!shownMobileNavModals.has(walletType)) {
            // Show modal and mark as shown for this session
            setShownMobileNavModals(prev => new Set(prev).add(walletType));
            showMobileNavModal(walletType, 'connect');
            return;
          }
        }
      }
    }

    // VALIDATION: Check if in-app browser type matches primary wallet type
    const isInSolflareApp = isInSolflareInAppBrowser();
    const isInBackpackApp = isInBackpackInAppBrowser();
    const isInMagicEdenApp = isInMagicEdenInAppBrowser();

    // Case 1: In Solflare in-app browser but primary wallet is not Solflare
    if (isInSolflareApp && primaryWallet.walletAdapter.toLowerCase() !== 'solflare') {
      setRequiredWalletType('Solflare');
      setMismatchModalVisible(true);
      return;
    }

    // Case 2: In Backpack in-app browser but primary wallet is not Backpack
    if (isInBackpackApp && primaryWallet.walletAdapter.toLowerCase() !== 'backpack') {
      setRequiredWalletType('Backpack');
      setMismatchModalVisible(true);
      return;
    }

    // Case 3: In Magic Eden in-app browser but primary wallet is not Magic Eden
    if (isInMagicEdenApp && primaryWallet.walletAdapter.toLowerCase() !== 'magiceden') {
      setRequiredWalletType('MagicEden');
      setMismatchModalVisible(true);
      return;
    }

    // GUARD: Don't reconnect if already connected, connecting, or just manually disconnected
    if (connected || connecting || isConnectingPhantom || isConnectingMWA || hasValidPhantomSession() || hasValidMWASession() || isManualDisconnect) {
      return;
    }

    // Case 3: No mismatch, proceed with normal connection
    connect(primaryWallet.walletAdapter as WalletAdapterName).then((publicKey) => {
      if (publicKey && publicKey.toBase58() !== primaryWallet.publicKey) {
        setWalletMismatchModal({
            show: true,
            primaryWallet,
            connectedWallet: {
                address: publicKey.toBase58(),
                adapter: selectedWallet,
            },
        });
      }
    }).catch((error) => {
      captureSentryException(error, {
        operation: 'wallet_auto_reconnect',
        walletAdapter: primaryWallet.walletAdapter,
        walletType: primaryWallet.walletType,
        isMobile: isMobileDevice(),
      });
    });
  }, [primaryWallet, connected, connecting, isConnectingPhantom, isConnectingMWA, hasValidPhantomSession, hasValidMWASession, mobileNavVisible, shownMobileNavModals, isAuthenticated, sessionValidated])

  // Check for pending mobile wallet connection on mount
  // This handles the case where user returns from mobile wallet app of Phantom wallet
  useEffect(function checkPendingConnectionPrimaryWalletPhantom() {
    const checkConnectionPendingConnection = async () => {
      const isMobile = isMobileDevice();
      const setPrimaryFlag = localStorage.getItem('set_primary_after_reconnect');

      if (!isMobile || !setPrimaryFlag) {
        clearPendingConnection();
        return;
      }

      let phantomResponse = parsePhantomConnectResponse();
      if (!phantomResponse) {
        const savedUrl = sessionStorage.getItem('redirectAfterLogin');
          if (savedUrl) {
            phantomResponse = parsePhantomConnectResponse(savedUrl);
          }
      }

      if (!phantomResponse) {
        return;
      }

      try {
        // Process the encrypted response
        const connectionData = processPhantomConnectResponse(phantomResponse);

        // Create PublicKey from the decrypted public_key
        const connectedPublicKey = new PublicKey(connectionData.public_key);

        // Update state to reflect connection
        setPublicKey(connectedPublicKey);
        setConnected(true);
        setSelectedWallet('Phantom');

        // Clear pending connection and URL parameters
        clearPendingConnection();

        // Clean up sessionStorage after successful connection
        sessionStorage.removeItem('redirectAfterLogin');

        // Remove URL parameters to clean up
        const url = new URL(window.location.href);
        url.searchParams.delete('phantom_encryption_public_key');
        url.searchParams.delete('data');
        url.searchParams.delete('nonce');
        url.searchParams.delete('errorCode');
        url.searchParams.delete('errorMessage');
        window.history.replaceState({}, document.title, url.toString());

        toast({
          title: 'Wallet connected successfully!',
          description: `Connected to Phantom`,
        });

        // Check if this reconnect was for setting primary wallet
        try {
          const { walletId } = JSON.parse(setPrimaryFlag);
          await apiRequest('POST', '/api/v1/wallet/set-primary', { walletId });
          toast({
            title: 'Primary Wallet Set',
            description: 'Wallet ownership verified and set as primary.',
          });
        } catch (error: any) {
          toast({
            title: 'Failed to Set Primary',
            description: error.message || 'Failed to set wallet as primary',
            variant: 'destructive',
          });
        } finally {
          localStorage.removeItem('set_primary_after_reconnect');
        }
      } catch (error: any) {
        clearPhantomSession();
        setIsConnectingPhantom(false);

        toast({
          title: 'Connection Failed',
          description: error.message || 'Failed to connect to Phantom',
          variant: 'destructive',
        });
      } finally {
        clearPendingConnection();
        sessionStorage.removeItem('redirectAfterLogin');
      }
    };

    checkConnectionPendingConnection();
  }, []);

  const getProvider = useCallback((walletName: WalletAdapterName): PhantomProvider | SolflareProvider | BackpackProvider | MagicEdenProvider | MWAProvider | null => {
    if (walletName?.toLowerCase() === 'phantom') {
      return window.phantom?.solana || null;
    } else if (walletName?.toLowerCase() === 'solflare') {
      return window.solflare || null;
    } else if (walletName?.toLowerCase() === 'backpack') {
      return window.backpack?.solana || null;
    } else if (walletName?.toLowerCase() === 'magiceden') {
      return window.magicEden?.solana || null;
    } else if (walletName?.toLowerCase() === 'mwa') {
      // Return MWA provider adapter (bridges Wallet Standard interface)
      return mwaProvider as MWAProvider;
    }
    return null;
  }, []);

  const select = useCallback((walletName: WalletAdapterName) => {
    setSelectedWallet(walletName);
  }, []);

  const handleMismatchModalClose = useCallback(() => {
    setMismatchModalVisible(false);
    setRequiredWalletType(null);
  }, []);

  const connect = useCallback(async (walletName?: WalletAdapterName): Promise<PublicKey> => {
    const targetWallet = walletName || selectedWallet;
    if (!targetWallet) {
      throw new Error('No wallet selected');
    }

    // Clear manual disconnect flag since user is connecting
    setIsManualDisconnect(false);

    setConnecting(true);
    try {
      const provider = getProvider(targetWallet);
      const isMobile = isMobileDevice();

      // MOBILE WALLET HANDLING
      if (isMobile && !provider && targetWallet) {
        let url: string = '';
        let redirectionMessage: string = '';
        let toastMessage: {
          title: string;
          description?: string;
        } = {
          title: '',
        }

        if (targetWallet?.toLowerCase() === 'phantom') {
          // PHANTOM: Generate deeplink to open Phantom app
          // User will approve in-app and return to website
          savePendingConnection(targetWallet);
          setIsConnectingPhantom(true);
          toastMessage = { title: 'Opening Phantom app...' };
          url = generatePhantomConnectDeeplink();
          redirectionMessage = 'Redirecting to Phantom app';
        } else if (targetWallet?.toLowerCase() === 'backpack') {
          toastMessage = {
            title: 'Opening Backpack',
            description: 'Please open this website in Backpack in-app browser',
          };
          url = generateBackpackInAppBrowserUrl();
          redirectionMessage = 'Redirecting to Backpack in-app browser';
        } else if (targetWallet?.toLowerCase() === 'solflare') {
          toastMessage = { title: 'Opening Solflare app...' };
          url = generateSolflareInAppBrowserUrl();
          redirectionMessage = 'Redirecting to Solflare in-app browser';
        } else if (targetWallet?.toLowerCase() === 'magiceden') {
          toastMessage = {
            title: 'Opening Magic Eden',
            description: 'Please open this website in Magic Eden in-app browser',
          };
          url = generateMagicEdenInAppBrowserUrl();
          redirectionMessage = 'Redirecting to Magic Eden in-app browser';
        }

        toast({
          title: toastMessage.title,
          description: toastMessage.description,
        });
        redirectToMobileWallet(url); 
        // Throw error to stop execution (user will be redirected)
        throw new Error(redirectionMessage);
      }

      // DESKTOP OR WALLET AVAILABLE ON MOBILE
      if (!provider) {
        // Show error message only (as per requirements)
        if (isMobile) {
          toast({
            title: 'Wallet Not Found',
            description: `${targetWallet} wallet not found. Please install the ${targetWallet} app.`,
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Wallet Not Found',
            description: `${targetWallet} wallet not found. Please install the extension or app.`,
            variant: 'destructive',
          });
        }
      }

      // Handle different wallet behaviors
      let connectedPublicKey: PublicKey;

      if (targetWallet?.toLowerCase() === 'phantom') {
        const response = await (provider as PhantomProvider)?.connect();
        connectedPublicKey = response.publicKey;
      } else if (targetWallet?.toLowerCase() === 'solflare') {
        // Solflare's connect() returns void, publicKey is accessed from provider.publicKey
        await (provider as SolflareProvider)?.connect();
        if (!provider?.publicKey) {
          throw new Error('Failed to get public key from Solflare');
        }
        connectedPublicKey = provider?.publicKey;
      } else if (targetWallet?.toLowerCase() === 'backpack') {
        const response = await (provider as BackpackProvider)?.connect();
        connectedPublicKey = response.publicKey;
      } else if (targetWallet?.toLowerCase() === 'magiceden') {
        const response = await (provider as MagicEdenProvider)?.connect();
        connectedPublicKey = response.publicKey;
      } else if (targetWallet?.toLowerCase() === 'mwa') {
        // MWA: Mobile Wallet Adapter (uses Wallet Standard)
        // MWA is mobile-only in our configuration
        if (!isMobile) {
          throw new Error('MWA is only available on mobile devices');
        }

        // Set connecting state - do NOT show a toast before connect() to ensure
        // the MWA Intent fires within the Android Chrome user-gesture window
        setIsConnectingMWA(true);

        try {
          const response = await (provider as MWAProvider)?.connect();
          connectedPublicKey = response.publicKey;

          // Success feedback
          toast({
            title: 'Wallet Connected!',
            description: 'Connected via Mobile Wallet Adapter',
          });
        } catch (mwaError: any) {
          setIsConnectingMWA(false);

          // Show user-friendly error toast
          toast({
            title: 'Connection Failed',
            description: mwaError.message || 'Could not connect to wallet. Please try again.',
            variant: 'destructive',
          });

          throw mwaError;
        } finally {
          setIsConnectingMWA(false);
        }
      } else {
        throw new Error('Unsupported wallet');
      }

      setPublicKey(connectedPublicKey);
      setConnected(true);
      setSelectedWallet(targetWallet);

      // Event listeners are now managed by the dedicated useEffect

      // Return the connected public key
      return connectedPublicKey;
    } catch (error) {
      throw error;
    } finally {
      setConnecting(false);
    }
  }, [selectedWallet, getProvider]);

  const disconnect = useCallback(async () => {
    if (!selectedWallet) return;

    // Set flag to prevent auto-reconnection
    setIsManualDisconnect(true);

    setDisconnecting(true);
    try {
      const provider = getProvider(selectedWallet);
      if (provider) {
        await provider.disconnect();
      }

      // Clear Phantom mobile session if it was a Phantom connection
      if (isMobileDevice() && selectedWallet?.toLowerCase() === 'phantom') {
        clearPhantomSession();
      }

      setPublicKey(null);
      setConnected(false);
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
      // Clear flag if disconnect failed
      setIsManualDisconnect(false);
      throw error;
    } finally {
      setDisconnecting(false);
    }
  }, [selectedWallet, getProvider]);

  const signTransaction = useCallback(async <T extends Transaction | VersionedTransaction>(
    transaction: T
  ): Promise<T> => {
    if (!selectedWallet) {
      throw new Error('No wallet connected');
    }

    const provider = getProvider(selectedWallet);
    const isMobile = isMobileDevice();

    // MOBILE SIGNING: Phantom deeplink
    if (isMobile && !provider && selectedWallet?.toLowerCase() === 'phantom') {
      // Note: For mobile signing, the caller should use the mobile utils directly
      // This is because signing requires handling the deeplink return flow
      // which is better managed at the component level
      throw new Error('Mobile signing should be handled by the component using mobile utils');
    }

    if (!provider) {
      throw new Error('Wallet provider not found');
    }

    return await provider.signTransaction(transaction);
  }, [selectedWallet, getProvider]);

  const signAllTransactions = useCallback(async <T extends Transaction | VersionedTransaction>(
    transactions: T[]
  ): Promise<T[]> => {
    if (!selectedWallet) {
      throw new Error('No wallet connected');
    }

    const provider = getProvider(selectedWallet);
    const isMobile = isMobileDevice();

    // MOBILE BATCH SIGNING: Phantom deeplink
    if (isMobile && !provider && selectedWallet?.toLowerCase() === 'phantom') {
      // Note: For mobile batch signing, the caller should use the mobile utils directly
      // This is because signing requires handling the deeplink return flow
      // which is better managed at the component level (e.g., for VIP batch minting)
      throw new Error('Mobile batch signing should be handled by the component using mobile utils');
    }

    if (!provider) {
      throw new Error('Wallet provider not found');
    }

    return await provider.signAllTransactions(transactions);
  }, [selectedWallet, getProvider]);

  const signMessage = useCallback(async (
    message: Uint8Array
  ): Promise<{ signature: Uint8Array }> => {
    if (!selectedWallet) {
      throw new Error('No wallet connected');
    }

    const provider = getProvider(selectedWallet);
    const isMobile = isMobileDevice();

    // MOBILE MESSAGE SIGNING: Phantom deeplink
    if (isMobile && !provider && selectedWallet?.toLowerCase() === 'phantom') {
      // Note: For mobile message signing, the caller should use the mobile utils directly
      // This is because signing requires handling the deeplink return flow
      // which is better managed at the component level
      throw new Error('Mobile message signing should be handled by the component using mobile utils');
    }

    if (!provider) {
      throw new Error('Wallet provider not found');
    }

    return await provider.signMessage(message);
  }, [selectedWallet, getProvider]);

  const sendTransaction = useCallback(async (
    transaction: Transaction,
    connection: any,
    options?: any
  ): Promise<string> => {
    if (!selectedWallet || !publicKey) {
      throw new Error('No wallet connected');
    }

    const provider = getProvider(selectedWallet);
    if (!provider) {
      throw new Error('Wallet provider not found');
    }

    // Sign the transaction
    const signedTransaction = await provider.signTransaction(transaction);

    // Serialize and send
    const rawTransaction = signedTransaction.serialize();
    const signature = await connection.sendRawTransaction(rawTransaction, options);

    return signature;
  }, [selectedWallet, publicKey, getProvider]);

  const walletContextValue: WalletContextState = {
    publicKey,
    connected,
    connecting,
    disconnecting,
    wallet: selectedWallet,
    connect,
    disconnect,
    select,
    signTransaction,
    signAllTransactions,
    signMessage,
    sendTransaction,
  };

  return (
    <WalletContext.Provider value={walletContextValue}>
      {children}
      {sessionValidated && isAuthenticated && (
        <>
          <InAppBrowserWalletMismatchModal
            visible={mismatchModalVisible}
            requiredWalletType={requiredWalletType}
            onClose={handleMismatchModalClose}
            onConnect={connect}
          />
          <MobileWalletNavigationModal
            visible={mobileNavVisible}
            walletType={mobileNavWalletType}
            context={mobileNavContext}
            onClose={hideMobileNavModal}
          />
        </>
      )}
    </WalletContext.Provider>
  );
};
